import React, { useMemo, useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { Ionicons } from '@expo/vector-icons';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../types/navigation';
import { useNavigationStyles } from '../styles/navigation';
import { useTheme } from '../styles/ThemeProvider';
import { isUserAdmin } from '../utils/userPermissions';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function WebNavHeader() {
  const theme = useTheme();
  const navigationStyles = useNavigationStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        // Brand-mark glyph. Hardcoded on purpose — does NOT spread any
        // theme.typography token or reference fontFamily.bodyMedium. The
        // logo is part of brand identity; future changes to the design
        // system should never alter it. Only `color` is theme-aware so
        // the glyph adapts to light/dark.
        brandLogo: {
          fontFamily: 'fontBrandLogo',
          fontSize: 22,
          lineHeight: 28,
          color: theme.colors.textPrimary,
        },
        addButton: {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme.colors.accent,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.lg,
          borderRadius: theme.radius.radiusMd,
          marginRight: theme.spacing.lg,
        },
        addIcon: {
          marginRight: theme.spacing.xs,
        },
        addButtonText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.backgroundPrimary,
        },
        avatarContainer: {
          position: 'relative',
          zIndex: 9999,
        },
        avatarButton: {
          padding: theme.spacing.xs,
          marginLeft: theme.spacing.sm,
        },
        dropdown: {
          position: 'absolute',
          top: 45,
          right: 0,
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          minWidth: 180,
          ...theme.shadows.cardShadow,
          zIndex: 10000,
        },
        dropdownItem: {
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
        },
        dropdownIcon: {
          marginRight: theme.spacing.md,
        },
        dropdownText: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
        },
        dropdownDivider: {
          height: 1,
          backgroundColor: theme.borders.borderColor,
        },
      }),
    [theme],
  );

  const navigation = useNavigation<NavigationProp>();
  const { session, user, signOut } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const dropdownRef = useRef<View>(null);

  useEffect(() => {
    if (session) {
      isUserAdmin().then(setIsAdmin);
    } else {
      setIsAdmin(false);
    }
  }, [session]);

  let avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  if (avatarUrl && avatarUrl.includes('googleusercontent.com')) {
    avatarUrl = avatarUrl.replace(/=s\d+-c$/, '=s200-c');
  }

  useEffect(() => {
    if (Platform.OS === 'web' && showDropdown) {
      const handleClickOutside = (event: any) => {
        setShowDropdown(false);
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showDropdown]);

  if (Platform.OS !== 'web') {
    return null;
  }

  const handleAvatarClick = (e: any) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleProfileClick = () => {
    setShowDropdown(false);
    navigation.navigate('Tabs' as any, { screen: 'Profile' });
  };

  const handleSignOutClick = async () => {
    setShowDropdown(false);
    await signOut();
  };

  return (
    <View style={navigationStyles.webHeader}>
      <View style={navigationStyles.webHeaderSection}>
        <TouchableOpacity
          style={navigationStyles.webBrandLink}
          onPress={() => session ? navigation.navigate('Tabs' as any, { screen: 'Cache', params: { screen: 'CacheList' } }) : navigation.navigate('Landing')}
          accessibilityRole="link"
          accessibilityLabel={session ? 'Go to Cache' : 'Cache home'}
        >
          <Text style={styles.brandLogo} accessible={false}>↗</Text>
        </TouchableOpacity>
      </View>

      <View style={navigationStyles.webHeaderSection}>
        {session && isAdmin && (
          <TouchableOpacity
            style={navigationStyles.webNavLink}
            onPress={() =>
              navigation.navigate('Tabs' as any, {
                screen: 'Buy',
                params: { screen: 'CatalogList' },
              })
            }
            accessibilityRole="link"
            accessibilityLabel="Buy marketplace"
          >
            <Text style={navigationStyles.webNavText}>buy</Text>
          </TouchableOpacity>
        )}

        {session && (
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => navigation.navigate('Main', { screen: 'AddItem' } as any)}
          >
            <Ionicons name="sparkles-outline" size={18} color={theme.colors.backgroundPrimary} style={styles.addIcon} />
            <Text style={styles.addButtonText}>Add to Cache</Text>
          </TouchableOpacity>
        )}

        {session ? (
          <View style={styles.avatarContainer} ref={dropdownRef}>
            <TouchableOpacity
              style={styles.avatarButton}
              onPress={handleAvatarClick}
            >
              {avatarUrl && !imageError ? (
                // @ts-ignore - Using native img element for web
                <img
                  src={avatarUrl}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    border: `1px solid ${theme.colors.border}`,
                    objectFit: 'cover',
                  }}
                  onError={() => setImageError(true)}
                  alt="Profile"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <Ionicons name="person-circle-outline" size={32} color={theme.colors.textPrimary} />
              )}
            </TouchableOpacity>

            {showDropdown && (
              <View style={styles.dropdown}>
                <TouchableOpacity
                  style={styles.dropdownItem}
                  onPress={handleProfileClick}
                >
                  <Ionicons name="person-outline" size={18} color={theme.colors.textPrimary} style={styles.dropdownIcon} />
                  <Text style={styles.dropdownText}>Profile</Text>
                </TouchableOpacity>

                <View style={styles.dropdownDivider} />

                <TouchableOpacity
                  style={styles.dropdownItem}
                  onPress={handleSignOutClick}
                >
                  <Ionicons name="log-out-outline" size={18} color={theme.colors.textPrimary} style={styles.dropdownIcon} />
                  <Text style={styles.dropdownText}>Sign Out</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ) : (
          <>
            <TouchableOpacity
              style={navigationStyles.webNavLink}
              onPress={() => navigation.navigate('SignIn', { redirect: undefined })}
            >
              <Text style={navigationStyles.webNavText}>Sign In</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={navigationStyles.webNavLink}
              onPress={() => navigation.navigate('SignUp')}
            >
              <Text style={navigationStyles.webNavText}>Sign Up</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
}
